import React, { useMemo } from 'react';
import type { Post } from '@app/types/core';
import formatDate from '@core/utils/formatDate';
import getContentSummary from '@core/utils/getContentSummary';
import { ScheduledPostsSummaryView } from '../../Shared/SchedulePost';

export interface ScheduledPostsSummaryProps {
  hasFinishedFetchingPosts: boolean;
  onChangeScheduledPost?: () => void;
  onOpenScheduledPostActions?: () => void;
  onViewScheduledPosts: () => void;
  scheduledPost: Post | null;
  scheduledPostCount: number;
}

const ATTACHMENT_SUMMARY_PATTERN = /^(?:📷|🎥|🎵|📁)\s/u;

const getScheduledPostContent = (post: Post) => {
  const summaries = (
    getContentSummary({ content: post.content, allBlocks: true }) || []
  )
    .filter(Boolean)
    .map((summary) => summary.trim());
  const attachmentSummaries = summaries.filter((summary) =>
    ATTACHMENT_SUMMARY_PATTERN.test(summary)
  );
  const attachmentCount = attachmentSummaries.length;
  const textSummaries = summaries.filter(
    (summary) => !ATTACHMENT_SUMMARY_PATTERN.test(summary)
  );
  const [title = '', ...previewBlocks] = textSummaries;

  return {
    attachmentCount,
    attachmentSummary:
      attachmentCount === 1 ? attachmentSummaries[0] : undefined,
    preview: previewBlocks.filter(Boolean).join(' ').trim(),
    title: title?.trim() || '',
  };
};

export const ScheduledPostsSummary = ({
  hasFinishedFetchingPosts,
  onChangeScheduledPost,
  onOpenScheduledPostActions,
  onViewScheduledPosts,
  scheduledPost,
  scheduledPostCount,
}: ScheduledPostsSummaryProps) => {
  const content = useMemo(
    () => (scheduledPost ? getScheduledPostContent(scheduledPost) : null),
    [scheduledPost]
  );

  if (!hasFinishedFetchingPosts || scheduledPostCount <= 0 || !scheduledPost) {
    return null;
  }

  return (
    <ScheduledPostsSummaryView
      actionsLabel="Scheduled post actions"
      attachmentCount={content?.attachmentCount || 0}
      attachmentSummary={content?.attachmentSummary}
      onManage={onChangeScheduledPost}
      onOpenActions={onOpenScheduledPostActions}
      onView={onViewScheduledPosts}
      preview={content?.preview || ''}
      scheduledDate={
        scheduledPost.scheduledFor ? formatDate(scheduledPost.scheduledFor) : ''
      }
      scheduledFor={scheduledPost.scheduledFor}
      summaryLabel="Next scheduled post"
      title={content?.title || ''}
      unavailableActionsLabel="Scheduled post actions unavailable"
      viewLabel={`View ${scheduledPostCount} scheduled post${
        scheduledPostCount === 1 ? '' : 's'
      }`}
    />
  );
};

export default ScheduledPostsSummary;
